import { z } from "zod";
import { ConvexTool } from "./index.js";
import { loadSelectedDeploymentCredentials } from "../../api.js";
import {
  RUN_ONEOFF_QUERY_SOURCE_DESCRIPTION,
  runTestFunctionQuery,
} from "../../runTestFunction.js";
import { ThrowingFetchError } from "../../utils/utils.js";
import { getMcpDeploymentSelection } from "../requestContext.js";

const inputSchema = z.object({
  deploymentSelector: z
    .string()
    .describe(
      "Deployment selector (from the status tool) to run the query on.",
    ),
  query: z.string().describe(RUN_ONEOFF_QUERY_SOURCE_DESCRIPTION),
});

const outputSchema = z.object({
  result: z.any().describe("The result returned by the query"),
  logLines: z
    .array(z.string())
    .describe("The log lines generated by the query"),
});

const description = `
Run a one-off readonly query on your Convex deployment.

This tool executes a JavaScript string as a query in your Convex deployment.
The query should follow Convex guidelines and use the following setup:

\`\`\`js
import { query, internalQuery } from "convex:/_system/repl/wrappers.js";

export default query({
  handler: async (ctx) => {
    console.log("Write and test your query function here!");
  },
});
\`\`\`

Note that there are no imports available in this environment. The only import
you can use is the built-in "convex:/_system/repl/wrappers.js" module in the
template.

The function call is also completely sandboxed, so it can only read data and
cannot modify the database or access the network.

Returns the result and any log lines generated by the query.
`.trim();

export const RunOneoffQueryTool: ConvexTool<
  typeof inputSchema,
  typeof outputSchema
> = {
  name: "runOneoffQuery",
  description,
  inputSchema,
  outputSchema,
  handler: async (ctx, args) => {
    const { projectDir, deployment } =
      await ctx.decodeDeploymentSelectorReadOnly(args.deploymentSelector);
    process.chdir(projectDir);
    const deploymentSelection = await getMcpDeploymentSelection(
      ctx,
      deployment,
    );
    const credentials = await loadSelectedDeploymentCredentials(
      ctx,
      deploymentSelection,
    );
    try {
      const outcome = await runTestFunctionQuery(ctx, {
        deploymentUrl: credentials.url,
        adminKey: credentials.adminKey,
        querySource: args.query,
      });
      if (outcome.kind === "applicationFailure") {
        return await ctx.crash({
          exitCode: 1,
          errorType: "fatal",
          printedMessage: `Query failed: ${JSON.stringify(outcome.payload, null, 2)}`,
        });
      }
      return {
        result: outcome.value,
        logLines: outcome.logLines,
      };
    } catch (err) {
      if (err instanceof ThrowingFetchError) {
        return await err.handle(ctx);
      }
      return await ctx.crash({
        exitCode: 1,
        errorType: "fatal",
        printedMessage: `Failed to run query: ${(err as Error).toString().trim()}`,
      });
    }
  },
};
